import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Check, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useDokobitFrame } from "@/domains/documents/signing/useDokobitFrame";
import {
  hasDeclinedSigner,
  isYourPartDone,
  openSignerTurn,
  pollSigningStatus,
  remainingSignerCount,
  RUNNING_SIGNING_STATES,
  type OpenSignerTurnResult,
  type SigningStatusResult,
} from "@/domains/documents/signing/repository";
import { refusalMessage } from "../refusalMessage";
import { track } from "@/lib/posthog";
import { reportOnce } from "@/lib/posthog/client";

interface SignTurnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
}

/**
 * Sign your turn in a chain, from the Signing page.
 *
 * This is what replaces PR 2232's dead end ("finish it in the window you
 * started it in"): `dokobit-sign-open` is a pure read that hands back the same
 * Gateway URL every time, so a turn can be picked up in any tab, any day —
 * which is the only way a chain somebody else started can ever be finished.
 *
 * The iframe plumbing is deliberately the same as SignDocumentsDialog's: the
 * same `useDokobitFrame` scripts, the same 3s status poll as the belt to
 * Dokobit's own postMessage callbacks, and the same "keep Dokobit's completion
 * screen mounted" behaviour (one iframe element across signing → completed).
 */
export function SignTurnDialog({ open, onOpenChange, sessionId }: SignTurnDialogProps) {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[75vw]">
        <DialogHeader>
          <DialogTitle>{t.signing.panel.signTurnTitle}</DialogTitle>
        </DialogHeader>
        {/* Remount per open so a reopened dialog re-reads the turn instead of
            showing a URL from a chain that may since have moved on. */}
        {open && <SignTurnFlow sessionId={sessionId} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

/** Why this dialog stopped: the chain moved on, or it finished outright. */
type Outcome = "handed_on" | "completed";

/** While the signing surface is up: fast, because the user is sitting on it. */
const POLL_MS = 3_000;
/**
 * Once your signature is in, this dialog stops being the thing that finalizes
 * the chain. The Signing page it always opens over re-reads the session as
 * `finalizing` and drives `dokobit-sign-status` on its own 3s timer
 * (useSigningAutoRefresh), so a fast tick here would mean two callers
 * reconciling one container. All the dialog still has to do is NOTICE the
 * finish, which the list's cadence is plenty for.
 */
const SEALING_POLL_MS = 15_000;
/**
 * 2 minutes of sealing — the same budget the Signing page's own poll gets
 * (MAX_FINALIZING_TICKS in useSigningItems), for the same reason. Finalization
 * is a download-and-upload of one container; past this, Dokobit or the backend
 * is stuck, and a spinner that never resolves reads as "my signature is lost".
 * The dialog stops asking and says what is actually true: it is recorded, the
 * container is still being sealed, and nothing is waiting on the user.
 *
 * Counted in COMPLETED SEALING reads: not in elapsed intervals, not in reads
 * that failed, and not in the read that DISCOVERED the signature — that one is
 * the last of the fast ticks and answered a different question. Anything else
 * spends the budget without ever having asked about the container, which is how
 * a flaky minute used to drop the user onto "still being sealed".
 */
const MAX_SEALING_TICKS = 8;
/**
 * The other way sealing never resolves: the reads themselves stop landing.
 *
 * {@link MAX_SEALING_TICKS} is spent in ANSWERS on purpose — a flaky minute
 * must not drop the user onto "still being sealed" having asked nothing. But
 * that alone leaves `dokobit-sign-status` being down (offline, 5xx, function
 * gone) as an unbounded spinner: no read ever completes, so no tick is ever
 * charged, which is exactly the "my signature is lost" outcome the budget
 * exists to prevent. So failures get their own, more generous budget —
 * 5 minutes of CONSECUTIVE failures, reset by any read that answers, so
 * flakiness still costs nothing and a real outage still ends.
 */
const MAX_SEALING_FAILURES = 20;

function signedDocumentProperties(status: SigningStatusResult) {
  const signers = status.signers ?? [];
  const yourSigner = signers.find((signer) => signer.is_you);
  return {
    signer_position: yourSigner ? yourSigner.position : 0,
    chain_size: signers.length,
  };
}

function reportSignedDocument(
  sessionId: string,
  status: SigningStatusResult,
  yourTurnWasOutstanding: boolean
) {
  if (!yourTurnWasOutstanding) return;
  if (status.state !== "completed" && !isYourPartDone(status)) return;
  if (!reportOnce(`document_signed:${sessionId}`)) return;

  track("document_signed", signedDocumentProperties(status));
}

function SignTurnFlow({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [turn, setTurn] = useState<OpenSignerTurnResult | null>(null);
  /**
   * The sentence this dialog ends on, and whether it is a FAILURE.
   *
   * Both kinds end the poll and replace the view, so they are one state — but
   * they must not look alike. "Your signature is recorded, the signing has since
   * been closed" exists to say the signature is safe and is not what failed; a
   * destructive icon over it says the opposite of the sentence under it.
   */
  const [notice, setNotice] = useState<{ text: string; failed: boolean } | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /**
   * Your signature is recorded but the chain has not closed — a solo signing,
   * or the last signer of one, waiting on Dokobit to assemble the container.
   * Tracked separately from `outcome` because the two things it decides differ:
   * the SIGNING SURFACE must come down (a mounted iframe after a recorded
   * signature invites signing twice and reads as "nothing happened"), while the
   * poll keeps running — slower, and only to notice the finish, since the list
   * behind the dialog now drives the reconcile.
   */
  const [signed, setSigned] = useState(false);
  /**
   * Somebody in the chain REFUSED. Nobody being pending then means the chain is
   * over, not that every signature is in — see the copy in the `signed` view.
   */
  const [declined, setDeclined] = useState(false);
  /** Ticks spent waiting for the container; only counted once `signed`. */
  const [sealingTicks, setSealingTicks] = useState(0);
  /** Consecutive failed reads while sealing; any answer resets it. */
  const [sealingFailures, setSealingFailures] = useState(0);
  /**
   * Dokobit's own `onSignError`, shown INLINE over the signing surface rather
   * than as a `notice`: it is not terminal. The signature may still have landed
   * (the callback is not always the last word), the user can retry in the frame
   * that is still mounted, and the poll — which is what refreshes the list and
   * notices the chain closing — has to keep running through it.
   */
  const [signError, setSignError] = useState<string | null>(null);
  const gaveUp =
    signed && (sealingTicks >= MAX_SEALING_TICKS || sealingFailures >= MAX_SEALING_FAILURES);
  const iframeUrl = turn ? turn.iframe_url : null;
  const { ready: frameReady, error: frameError } = useDokobitFrame(iframeUrl != null);
  const finalizedRef = useRef(false);
  const signedRef = useRef(false);
  /**
   * A read has seen your part still OUTSTANDING, so a later read finding it done is a
   * signature this dialog WATCHED land — the only thing `document_signed` may count.
   *
   * The dialog remounts per open and `dokobit-sign-open` is a pure read that hands back
   * the same URL however often it is called, so without this, reopening a session you
   * already signed (a stale row still offering "Sign now", a user checking on the chain)
   * emits the event again for one signature. This ref is per mount and so cannot see the
   * previous open; `reportOnce(document_signed:<sessionId>)` is the bound that survives
   * one, and the name is unrenameable, so a duplicate in the funnel would be permanent.
   *
   * Seeded by a read taken the moment the turn opens (below), never left to the poll: the
   * first scheduled tick is POLL_MS away and every tick while the tab is hidden is skipped,
   * and Smart-ID / mobile-ID backgrounds the tab BY DESIGN — so the most common successful
   * signature is precisely the one no tick observed outstanding, and it would report
   * nothing. A seeding read that fails to ANSWER leaves no baseline, and defaults this to
   * true rather than to false — see the effect for which way that error is worth making.
   */
  const yourTurnOutstandingRef = useRef(false);
  /** Whether that seeding read has been issued — one per opened turn, not per effect run. */
  const seededRef = useRef(false);
  /** The status read currently outstanding, if any — see `pollNow`. */
  const readRef = useRef<Promise<boolean> | null>(null);
  const isDone = outcome !== null;

  // The whole page caches under the "signing" prefix with a 5-min staleTime, so
  // without this the row behind the dialog still reads "waiting for you".
  // Only ever called off a status READ, never off Dokobit's own callback:
  // invalidating before the server has recorded anything just refetches the
  // pre-signature row and pins the list to it.
  const refreshList = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["signing"] });
  }, [queryClient]);

  const finish = useCallback(
    (how: Outcome) => {
      if (finalizedRef.current) return;
      finalizedRef.current = true;
      setOutcome(how);
      refreshList();
    },
    [refreshList]
  );

  // Open the turn once. A refusal (403) is terminal and explains itself; a 404
  // from a not-yet-deployed function arrives as a plain Error and shows its
  // message rather than white-screening the page.
  useEffect(() => {
    let cancelled = false;
    openSignerTurn(sessionId)
      .then((opened) => {
        if (!cancelled) setTurn(opened);
      })
      .catch((error: unknown) => {
        if (!cancelled)
          setNotice({
            text: refusalMessage(error, t.signing, t.signing.panel.signTurnFailed),
            failed: true,
          });
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, t]);

  // Cancelled / failed / expired sessions use stable translated copy rather
  // than backend prose. If the signature landed first, retain that fact and its
  // non-error tone while still closing the stale list row.
  const closeEndedSession = useCallback(() => {
    setNotice(
      signedRef.current
        ? { text: t.signing.panel.signTurnClosedAfterSigning, failed: false }
        : { text: t.signing.refusal.chainClosed, failed: true }
    );
    refreshList();
  }, [refreshList, t]);

  // A running response either seeds the pending baseline, hands the chain to a
  // co-signer, or removes the signing surface while the last/solo container seals.
  const applyRunningStatus = useCallback(
    (status: SigningStatusResult) => {
      setDeclined(hasDeclinedSigner(status));
      if (!isYourPartDone(status)) {
        yourTurnOutstandingRef.current = true;
        return;
      }
      if (remainingSignerCount(status) > 0) {
        finish("handed_on");
        return;
      }
      if (signedRef.current) return;

      signedRef.current = true;
      setSigned(true);
      refreshList();
    },
    [finish, refreshList]
  );

  /**
   * Read once and reconcile the chain. A solo/last signature ends the signing
   * surface but keeps this dialog polling until the container seals. The result
   * says whether a response arrived, because failed reads spend no sealing tick.
   */
  const readStatus = useCallback(
    async (reuseRecent: boolean) => {
      try {
        const status = await pollSigningStatus(sessionId, { reuseRecent });
        reportSignedDocument(sessionId, status, yourTurnOutstandingRef.current);
        if (status.state === "completed") {
          finish("completed");
          return true;
        }
        if (!RUNNING_SIGNING_STATES.has(status.state)) {
          closeEndedSession();
          return true;
        }
        applyRunningStatus(status);
        return true;
      } catch {
        return false;
      }
    },
    [applyRunningStatus, closeEndedSession, finish, sessionId]
  );

  /**
   * The single-flight wrapper every read goes through. Two entry points reach
   * it — the timer below and Dokobit's `onSignSuccess`, which fires whenever it
   * likes and can fire more than once — so the timer's own no-overlap
   * scheduling is not enough on its own: a callback landing mid-tick would run
   * a SECOND `reconcileChain` (a container download and upload) against one
   * session. A caller arriving while a read is outstanding joins that read
   * instead of starting another, so the poll budget still spends one tick per
   * completed read.
   */
  const pollNow = useCallback(
    (reuseRecent = signedRef.current) => {
      if (!readRef.current) {
        readRef.current = readStatus(reuseRecent).finally(() => {
          readRef.current = null;
        });
      }
      return readRef.current;
    },
    [readStatus]
  );

  // One read as soon as the turn opens, outside the poll's cadence and its hidden-tab
  // skip. It is what seeds `yourTurnOutstandingRef` while the signature is still
  // outstanding — the poll cannot: the user leaves for the Smart-ID / mobile-ID app
  // within the first 3s and the tab is hidden from then until the signature is already
  // in. Reuse a recent list read for this baseline to avoid reconciling the same
  // container again on open. Normal polling still reads fresh before signing;
  // a reopened session you already signed reports nothing.
  useEffect(() => {
    if (!turn || seededRef.current) return;
    seededRef.current = true;
    void pollNow(true).then((answered) => {
      // A seed that never got an ANSWER (offline, a 5xx, an auth refresh mid-request) leaves
      // no baseline at all, and leaving the ref false then means the signature that follows
      // reports nothing — a silent undercount, in the direction that hides problems. So an
      // unanswered seed defaults to counting, against losing real signatures every time the
      // network blinks as the dialog opens. Within this page load it can no longer cost a
      // duplicate: what it reaches is `reportOnce` on the session id, spent for good by the
      // first report of this signature however often the dialog is reopened. `reportedKeys`
      // is module state, so a reload, a second tab or a post-sign-out session starts empty —
      // still the right way round, losing real signatures is worse than a rare duplicate.
      if (!answered) yourTurnOutstandingRef.current = true;
    });
  }, [turn, pollNow]);

  // Self-rescheduling rather than setInterval, the same shape the Signing
  // page's poll uses (useSigningAutoRefresh) and for the same two reasons.
  // `pollSigningStatus` runs `reconcileChain` server-side — a container
  // download and upload — which in the slow-finalize case this poll exists for
  // routinely outlives its own gap, so an interval would stack concurrent
  // reconciles of one session from one tab. And the budget has to be spent in
  // completed reads: counting fires would expire the 8 ticks after far fewer
  // status reads than intended, exactly when responses are slowest.
  useEffect(() => {
    if (!turn || isDone || notice || gaveUp) return;
    let cancelled = false;
    let timer = 0;

    const schedule = () => {
      timer = window.setTimeout(
        async () => {
          // A hidden tab is not being read, and the pre-signature poll has no
          // tick cap — `gaveUp` needs `signed` — so a dialog left open on a
          // background tab (start signing, get interrupted, walk away) would
          // drive `dokobit-sign-status`, i.e. a server-side reconcile with a
          // container download and upload, every 3s indefinitely. Same skip the
          // Signing page's own poll makes (useSigningAutoRefresh), for the same
          // reason: nothing a hidden tick resolves is anything the first
          // foreground tick would not, one cadence later.
          if (document.visibilityState !== "visible") {
            schedule();
            return;
          }
          // Read BEFORE the await: the tick that discovers the signature flips
          // the ref mid-flight, and charging it to the sealing budget would
          // spend a fast 3s tick as though it were one of the 15s waits for the
          // container — 7 sealing reads instead of 8, and 105s of the 120s the
          // budget is written as.
          const sealing = signedRef.current;
          const answered = await pollNow();
          if (cancelled) return;
          // Budget spent only after your signature lands: before that the poll
          // is what notices the chain being cancelled under you, and the user
          // is sitting on a live signing surface, so it must not time out.
          // A read that answered spends a tick and clears the failure streak; a
          // read that did not spends only the failure budget, which is what
          // stops a permanently unreachable status endpoint spinning forever.
          if (sealing) {
            if (answered) {
              setSealingTicks((spent) => spent + 1);
              setSealingFailures(0);
            } else {
              setSealingFailures((failed) => failed + 1);
            }
          }
          schedule();
        },
        signed ? SEALING_POLL_MS : POLL_MS
      );
    };

    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [turn, isDone, notice, gaveUp, signed, pollNow]);

  // Let Dokobit's own callbacks short-circuit the poll wait — by taking a
  // status READ early, never by declaring the dialog finished on its own. That
  // holds for `onSignError` too: it reports that ONE attempt failed, not that
  // the signing is over, and it is not always the last word — a signature can
  // still land after it. Ending the dialog on it would stop the poll that
  // refreshes the list and that notices the chain actually closing, so the
  // error is shown over a live signing surface the user can retry in, and the
  // poll carries on deciding where this session really landed.
  //
  // `Isign` is a GLOBAL shared with the sibling SignDocumentsDialog, and these
  // handlers close over this dialog. Left installed after it closes, a late
  // postMessage would run a status read — i.e. a server-side reconcile — for a
  // session nothing is watching, and set state on an unmounted tree. Cleanup
  // clears only handlers still ours: whoever mounted after us owns theirs.
  useEffect(() => {
    const isign = window.Isign;
    if (!iframeUrl || !frameReady || !isign) return;
    const onSignSuccess = () => void pollNow();
    const onSignError = () => setSignError(t.signing.panel.signTurnSignFailed);
    isign.onSignSuccess = onSignSuccess;
    isign.onSignError = onSignError;
    // Keep the frame at the contained height below instead of letting Dokobit
    // grow it to its full content height.
    isign.adjustHeight = false;
    isign.PostMessage?.init?.();
    return () => {
      if (isign.onSignSuccess === onSignSuccess) isign.onSignSuccess = undefined;
      if (isign.onSignError === onSignError) isign.onSignError = undefined;
    };
  }, [iframeUrl, frameReady, pollNow, t]);

  if (notice) {
    // A recorded signature is never drawn as a failure — see `notice`.
    const Icon = notice.failed ? AlertCircle : Check;
    return (
      <div className="flex flex-col items-center gap-3 py-10">
        <Icon
          className={cn("h-10 w-10", notice.failed ? "text-destructive" : "text-success")}
          aria-hidden="true"
        />
        <p className="max-w-sm text-center text-sm text-muted-foreground">{notice.text}</p>
        <Button variant="outline" onClick={onClose}>
          {t.common.close}
        </Button>
      </div>
    );
  }

  if (!turn) {
    return (
      <div className="flex flex-col items-center gap-3 py-10">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  }

  // Your part is done. Say so, and take the iframe away — leaving a signing
  // surface up after a recorded signature invites signing twice, and reads as
  // "nothing happened". Which sentence depends on where the chain actually
  // landed: promising "it moves on to the next signer" when you were the last
  // one (or the only one) describes a hand-off that never happens.
  if (isDone) {
    return (
      <div className="flex flex-col gap-4 py-6">
        <div className="flex items-center gap-2 text-sm">
          <Check className="h-5 w-5 shrink-0 text-success" aria-hidden="true" />
          <span>
            {outcome === "completed"
              ? t.signing.panel.signTurnCompleted
              : t.signing.panel.signTurnDone}
          </span>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            {t.common.close}
          </Button>
        </div>
      </div>
    );
  }

  // Your signature is recorded; the container is not built yet. The iframe goes
  // NOW — the same reason it goes when the dialog is done — while a slower poll
  // behind this view waits for the container the list is already driving.
  // Closing here is safe: the page's own auto-refresh picks the session up.
  // Past the budget the spinner is a lie — the sentence below says what is
  // true (recorded, still sealing) instead of implying it is about to resolve.
  if (signed) {
    // A refusal in the chain makes "every signature is in" false — the same
    // sentence the list's panel picks between, for the same reason. It decides
    // the given-up sentence too: "it will appear with your documents once it is
    // ready" is false BY CONSTRUCTION on a declined chain (computeChainAdvance
    // fails it, so no container is ever filed), and saying it after two minutes
    // of `finalizingAfterDecline` contradicts the dialog's own last screen.
    const waiting = declined ? t.signing.panel.finalizingAfterDecline : t.signing.panel.finalizing;
    const stopped = declined
      ? t.signing.panel.signTurnSealingAfterDecline
      : t.signing.panel.signTurnSealing;
    return (
      <div className="flex flex-col gap-4 py-6">
        <div className="flex items-center gap-2 text-sm">
          {gaveUp ? (
            <Check className="h-5 w-5 shrink-0 text-success" aria-hidden="true" />
          ) : (
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-success" aria-hidden="true" />
          )}
          <span>{gaveUp ? stopped : waiting}</span>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            {t.common.close}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">{t.signing.panel.signTurnHint}</p>
      {frameError && <p className="text-xs text-destructive">{frameError}</p>}
      {signError && <p className="text-xs text-destructive">{signError}</p>}
      {iframeUrl && (
        // Same frame, same treatment as SignDocumentsDialog — see the long note
        // there. No border/radius (the dialog is the container, and the sections
        // inside are flattened by the hosted stylesheet), and `colorScheme:
        // light` because that stylesheet's `prefers-color-scheme: light` half
        // resolves against THIS element, not the visitor's OS. Without the pin a
        // dark-preference user gets the fills stripped with no white body forced
        // underneath.
        <iframe
          id="isign-gateway"
          title={t.signing.panel.signTurnTitle}
          src={iframeUrl}
          style={{ colorScheme: "light" }}
          className="h-[65vh] w-full overflow-auto"
        />
      )}
      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose}>
          {isDone ? t.common.close : t.common.cancel}
        </Button>
      </div>
    </div>
  );
}
